<template>
  <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>

  <WdsStack direction-column gap-md>
    <div>
      <WdsStack direction-column gap-sm>
        <WdsInput
          v-model="name.firstName"
          :disabled="nameInputDisabled || loading"
          :label="$t('Name')"
          :placeholder="$t('First name')"
          :error="errors.firstName"
          data-test="tc-first-name"
          required
        />
        <WdsInput
          v-model="name.middleName"
          :disabled="nameInputDisabled || loading"
          :placeholder="$t('Middle name')"
          :error="errors.middleName"
          data-test="tc-middle-name"
        />
        <WdsInput
          v-model="name.lastName"
          :disabled="nameInputDisabled || loading"
          :placeholder="$t('Last name')"
          :error="errors.lastName"
          data-test="tc-last-name"
          required
        />
      </WdsStack>
    </div>

    <div>
      <WdsInput
        v-model="email"
        :disabled="emailInputDisabled || loading"
        :label="$t('E-mail')"
        :placeholder="$t('E-mail address')"
        :error="errors.email"
        data-test="tc-mail"
        required
      />
    </div>

    <div>
      <WdsStack direction-column gap-sm>
        <WdsText :text="$t('Links')" />

        <div v-for="(link, index) in links" :key="link.key">
          <WdsStack align-start gap-sm>
            <div class="tw-grow">
              <WdsInput
                v-model="links[index].url"
                :disabled="loading"
                :error="errors?.[`links.${index}`]"
                :data-test="`tc-link-input-${index}`"
                prefix-icon="link"
              />
            </div>

            <WdsButtonGhost
              class="!tw-mt-2 tw-pt"
              :data-test="`tc-link-remove-${index}`"
              @click="removeLink(index)"
            >
              <WdsIcon name="close" size="16" class="tw-text-gray-600" />
            </WdsButtonGhost>
          </WdsStack>
        </div>

        <div>
          <WdsButtonGhost primary data-test="tc-link-add" :disabled="loading" @click="addLink('')">
            <WdsStack align-center gap-sm>
              <WdsIcon name="plus" size="18" />
              {{ !links.length ? $t('Add link') : $t('Add another link') }}
            </WdsStack>
          </WdsButtonGhost>
        </div>
      </WdsStack>
    </div>

    <div>
      <WdsStack direction-column gap-sm>
        <WdsText :text="$t('Attachments')" />

        <div>
          <WdsStack v-for="attachment in attachments" :key="attachment.id" align-center gap-sm>
            <WdsIcon name="document" size="16" class="tw-shrink-0" />

            <a
              :href="laravelNamedRoute(namedRoutes.files.download, { fileId: attachment.id })"
              class="tw-font-medium tw-grow tw-line-clamp-1 tw-break-all"
              :data-test="`tc-file-${attachments.indexOf(attachment)}`"
              target="_blank"
              :title="attachment.name"
            >
              {{ attachment.name }}
            </a>

            <WdsButtonGhost
              :data-test="`tc-file-remove-${attachments.indexOf(attachment)}`"
              @click="removeAttachment(attachment.id)"
            >
              <WdsIcon name="close" size="16" class="tw-text-gray-600" />
            </WdsButtonGhost>
          </WdsStack>
        </div>

        <div>
          <PickerFileFilebank
            v-slot="{ openFilePicker }"
            :exclude-ids="attachments.map(({ id }) => id)"
            use-global-id
            @files-selected="addAttachments"
          >
            <WdsButtonGhost
              primary
              :disabled="loading"
              class="tw-flex tw-items-center tw-gap-x-2"
              data-test="tc-file-add"
              @click="openFilePicker"
            >
              <WdsIcon name="plus" size="18" />
              {{ !attachments.length ? $t('Add file') : $t('Add another file') }}
            </WdsButtonGhost>
          </PickerFileFilebank>
        </div>
      </WdsStack>
    </div>

    <div>
      <WdsStack direction-column gap-sm>
        <WdsText :text="$t('Skills')" />

        <TypeSelect
          name="skills"
          tag-type="skills"
          :placeholder="$t('Add skills')"
          :selected-in="skills"
          :show-typesuggestions="false"
          :can-add-unsupported-types="true"
          data-test="tc-skills-input"
        />
      </WdsStack>
    </div>

    <div>
      <WdsStack direction-column gap-sm>
        <WdsText :text="$t('External ID')" />
        <WdsInput
          v-if="displayExternalIdentifierInput"
          v-model="externalIdentifier"
          :disabled="loading"
          :data-test="`tc-external-identifier-input`"
          :placeholder="$t('Add external ID')"
        ></WdsInput>
        <div v-if="!displayExternalIdentifierInput">
          <WdsButtonGhost
            primary
            data-test="tc-link-add"
            :disabled="loading"
            @click="addExternalIdentifier"
          >
            <WdsStack align-center gap-sm>
              <WdsIcon name="plus" size="18" />
              {{ $t('Add external ID') }}
            </WdsStack>
          </WdsButtonGhost>
        </div>
      </WdsStack>
    </div>

    <WdsAlert v-show="errors.customFieldValues" error>
      {{ errors.customFieldValues }}
    </WdsAlert>
    <FieldableFormCustomFields
      v-model:field-values="customFieldValues"
      :context-message="
        $t('Details specified below will only be visible to you and your team members.')
      "
      :account-id="currentAccountId"
      :fieldable-type="TypeSupportingCustomFieldValues.TrustedContact"
    />

    <div v-if="!messageInputHidden">
      <WdsInputArea
        v-model="message"
        :label="$t('Message')"
        :placeholder="$t('Write a welcome message to the Trusted Contact')"
        :error="errors.message"
        data-test="tc-message-input"
        height="60px"
        required
      />
    </div>
  </WdsStack>

  <WdsStack justify-space-between class="tw-mt-7">
    <WdsButton tertiary data-test="tc-manage-cancel" @click="onCancel">
      {{ $t('Cancel') }}
    </WdsButton>

    <WdsStack align-center gap-md>
      <WdsSpinner v-if="loading" indeterminate />

      <WdsButton
        v-if="isEditing"
        primary
        data-test="tc-save"
        :disabled="loading"
        @click="handleOnConfirm('update')"
      >
        {{ $t('Save') }}
      </WdsButton>

      <WdsButton
        v-if="!isEditing"
        secondary
        data-test="tc-invite-and-offer"
        :disabled="loading"
        @click="handleOnConfirm('inviteAndOffer')"
      >
        {{ $t('Invite and hire') }}
      </WdsButton>

      <WdsButton
        v-if="!isEditing"
        primary
        data-test="tc-invite-submit"
        :disabled="loading"
        @click="handleOnConfirm('inviteOnly')"
      >
        {{ $t('Invite') }}
      </WdsButton>
    </WdsStack>
  </WdsStack>
</template>

<script setup>
import { computed, defineProps, ref } from 'vue'
import {
  WdsAlert,
  WdsStack,
  WdsButton,
  WdsButtonGhost,
  WdsSpinner,
  WdsIcon,
  WdsText,
  WdsInput,
  WdsInputArea,
} from '@worksome/wds-components'

import TypeSelect from '@/components/TypeSelect.vue'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { FormValidation } from '@/utils/formValidation'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { mapCustomFieldValueToCustomFieldValueInput } from '@/components/Fieldable/utils'
import { useTranslation } from '@/composables/translation'

const props = defineProps({
  onCancel: {
    type: Function,
    required: true,
  },
  onConfirm: {
    type: Function,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  initialName: {
    type: Object,
    default: () => ({
      firstName: '',
      middleName: '',
      lastName: '',
    }),
    validator: (obj) =>
      ['firstName', 'middleName', 'lastName'].every(
        (key) => !!obj[key] && typeof obj[key] === 'string',
      ),
  },
  initialEmail: {
    type: String,
    default: '',
  },
  initialLinks: {
    type: Array,
    default: () => [],
  },
  initialAttachments: {
    type: Array,
    default: () => [],
  },
  initialSkills: {
    type: Array,
    default: () => [],
  },
  initialExternalIdentifier: {
    type: String,
    default: '',
  },
  initialCustomFieldValues: {
    type: Array,
    default: () => [],
  },
  nameInputDisabled: {
    type: Boolean,
    default: false,
  },
  emailInputDisabled: {
    type: Boolean,
    default: false,
  },
  messageInputHidden: {
    type: Boolean,
    default: false,
  },
})

const { $t } = useTranslation()

const currentAccountId = ref(currentAccount.globalId)

const loading = ref(false)

const errorBannerMessage = ref(null)
const errors = ref({})

const name = ref(props.initialName)
const email = ref(props.initialEmail)
const links = ref(props.initialLinks)
const attachments = ref(props.initialAttachments)
const skills = ref(props.initialSkills)
const externalIdentifier = ref(props.initialExternalIdentifier)
const customFieldValues = ref(
  mapCustomFieldValueToCustomFieldValueInput(props.initialCustomFieldValues),
)
const message = ref($t('Join us on Worksome. Complete your registration so we can work together!'))

const resetFields = () => {
  name.value = props.initialName
  email.value = props.initialEmail
  links.value = props.initialLinks
  attachments.value = props.initialAttachments
  skills.value = props.initialSkills
  externalIdentifier.value = props.initialExternalIdentifier
  customFieldValues.value = props.initialCustomFieldValues
  message.value = ''
}

const resetErrors = () => {
  errors.value = {}
  errorBannerMessage.value = null
}

const hasSubmitted = ref(false)

const externalIdentifierIsManuallyToggled = ref(false)
const displayExternalIdentifierInput = computed(
  () => externalIdentifier.value || externalIdentifierIsManuallyToggled.value,
)
const addExternalIdentifier = () => {
  externalIdentifierIsManuallyToggled.value = true
}

const addLink = (url) => {
  const hasEmptyRows = links.value.some((link) => link.url === '')

  if (!hasEmptyRows) {
    links.value = [...links.value, { key: links.value.length, url }]
  }
}

const removeLink = (linkIndex) => {
  links.value.splice(linkIndex, 1)

  delete errors.value?.[`input.links.${linkIndex}`]
}

const addAttachments = (selectedAttachments) => {
  attachments.value = [
    ...attachments.value,
    ...selectedAttachments.map((selectedAttachment) => ({
      name: selectedAttachment.name,
      id: selectedAttachment.id,
      storageName: selectedAttachment.storageName,
    })),
  ]
}

const removeAttachment = (attachmentId) => {
  attachments.value.splice(
    attachments.value.map((attachment) => attachment.id).indexOf(attachmentId),
    1,
  )
}

async function handleOnConfirm(action) {
  loading.value = true

  await props
    .onConfirm(
      {
        name,
        email,
        links,
        attachments,
        skills,
        message,
        externalIdentifier,
        customFieldValues,
      },
      action,
    )
    .then(() => {
      resetFields()
      resetErrors()
    })
    .catch((error) => {
      hasSubmitted.value = true
      resetErrors()

      const { graphQLErrors } = error

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      loading.value = false
    })
}
</script>

<style scoped lang="scss">
:deep(label) {
  @apply tw-mb-0;
}
</style>
